import { Avatar, Box, Checkbox, HStack, Tabs, Text, VStack } from '@chakra-ui/react'
import { useState } from 'react'
import { ServiceStatBoxes, type ServiceValues } from './ServiceStatBoxes'

interface Props {
  sibName: string

  /** Vereinbarte Leistungen (units) — the "aus Y" reference for Restkapazität. */
  agreedCapacity: ServiceValues | null
  /** Übriges Leistungskontingent laufendes Jahr (units). */
  remainingCurrentYear: ServiceValues | null

  /** Leistungsumfang aller offenen Bestellungen (units). */
  openOrdersUnits: ServiceValues | null
  /** Bruttogesamtwert aller offenen Bestellungen (EUR). */
  openOrdersGross: number | null

  /** Preise pro Einheit (brutto), keyed by service code. */
  unitPrices: ServiceValues | null

  useAllOpenOrders: boolean
  onToggle: (checked: boolean) => void
}

function formatEUR(value: number) {
  return value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })
}

export function SelectedSibCapacity({
  sibName,
  agreedCapacity,
  remainingCurrentYear,
  openOrdersUnits,
  openOrdersGross,
  unitPrices,
  useAllOpenOrders,
  onToggle,
}: Props) {
  const [tab, setTab] = useState<'capacity' | 'prices'>('capacity')

  return (
    <VStack align="stretch" gap={5}>
      {/* Profile header (email omitted — not exposed by the API) */}
      <HStack gap={3}>
        <Avatar.Root size="md" variant="subtle" colorPalette="blue">
          <Avatar.Fallback name={sibName} />
        </Avatar.Root>
        <Text fontSize="lg" fontWeight="semibold" color="fg.default">
          {sibName}
        </Text>
      </HStack>

      <Tabs.Root
        value={tab}
        onValueChange={e => setTab(e.value as 'capacity' | 'prices')}
        variant="subtle"
        size="sm"
      >
        <Tabs.List bg="bg.muted" borderRadius="md" gap={1} w="full">
          <Tabs.Trigger
            value="capacity"
            flex="1"
            justifyContent="center"
            _selected={{ bg: 'bg', color: 'fg.default', shadow: 'sm' }}
          >
            Kapazität
          </Tabs.Trigger>
          <Tabs.Trigger
            value="prices"
            flex="1"
            justifyContent="center"
            _selected={{ bg: 'bg', color: 'fg.default', shadow: 'sm' }}
          >
            Preise
          </Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="capacity">
          <VStack align="stretch" gap={4} pt={2}>
            <Box>
              <Text fontSize="sm" color="fg.muted" mb={2}>
                Restkapazität
              </Text>
              <ServiceStatBoxes
                values={remainingCurrentYear ?? {}}
                capacities={agreedCapacity ?? {}}
                variant="capacity"
              />
            </Box>

            <Checkbox.Root
              checked={useAllOpenOrders}
              onCheckedChange={d => onToggle(d.checked === true)}
              w="full"
              justifyContent="space-between"
              gap={3}
              px={4}
              py={3}
              borderWidth="1px"
              borderColor="border"
              borderRadius="lg"
              bg="bg.muted"
              cursor="pointer"
            >
              <Checkbox.HiddenInput />
              <Checkbox.Label m={0} fontSize="sm" fontWeight="medium" color="fg.default">
                Ergebnis aller offenen Bestellungen
              </Checkbox.Label>
              <Checkbox.Control />
            </Checkbox.Root>

            <Box>
              <Text fontSize="sm" color="fg.muted" mb={2}>
                Leistungsumfang aller offenen Bestellungen
              </Text>
              <ServiceStatBoxes values={openOrdersUnits ?? {}} />
            </Box>

            <Box>
              <Text fontSize="sm" color="fg.muted" mb={1}>
                Bruttogesamtwert aller offenen Bestellungen
              </Text>
              <Text fontSize="lg" fontWeight="semibold" color="fg.default">
                {openOrdersGross != null ? formatEUR(openOrdersGross) : '—'}
              </Text>
            </Box>
          </VStack>
        </Tabs.Content>

        <Tabs.Content value="prices">
          <VStack align="stretch" gap={4} pt={2}>
            <Box>
              <Text fontSize="sm" color="fg.muted" mb={2}>
                Preise pro Einheit (brutto)
              </Text>
              {unitPrices ? (
                <ServiceStatBoxes
                  values={unitPrices}
                  formatValue={formatEUR}
                  valueFontSize="md"
                />
              ) : (
                <Text fontSize="sm" color="fg.subtle">
                  Keine Preise hinterlegt.
                </Text>
              )}
            </Box>
          </VStack>
        </Tabs.Content>
      </Tabs.Root>
    </VStack>
  )
}
